import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import { getWindow } from 'src/utilities/window';
import { getTypeaheadStyle } from 'src/utilities/typeaheadStyle';

export interface IDropdownProps {
  /**
   * Stateless component for the toggle.
   */
  toggle: ({ expanded }: { expanded: boolean }) => JSX.Element;

  /**
   * Stateless component for the menu.
   */
  menu: ({ show }: { show: boolean }) => JSX.Element;

  className?: string;

  /**
   * Start open.
   */
  openOnInit?: boolean;

  /**
   * Disable clicks.
   */
  disabled?: boolean;
  closeMenuOnClick?: boolean;
  isFullWidth?: boolean;
  maxMenuHeight?: string;
  isMenuRightAligned?: boolean;
  isMenuCentered?: boolean;
  onToggle?: (isOpen: boolean) => void;
  error?: boolean;
  menuWidth?: string;
  isPortalMenu?: boolean;
}

export interface IDropdownToggleProps {
  /**
   * True when the menu is open.
   *
   * The implementing component should apply
   * aria-haspopup: true at all times and aria-expanded: true to itself when expanded.
   */
  expanded: boolean;
}

export interface IDropdownMenuProps {
  /**
   * True when the menu is open.
   *
   * The implementing component should apply '.dropdown-menu' at all times, and '.show' when this is true.
   */
  show: boolean;
}

export const Dropdown = forwardRef(
  (
    {
      className,
      toggle,
      menu,
      openOnInit,
      disabled,
      closeMenuOnClick = true,
      isFullWidth,
      maxMenuHeight,
      isMenuRightAligned,
      onToggle,
      error = false,
      isMenuCentered,
      menuWidth,
      isPortalMenu,
    }: IDropdownProps,
    ref
  ): JSX.Element => {
    const toggleRef = useRef(null);
    const wrapToggleRef = useRef(null);
    const window = getWindow();
    const [open, setOpen] = useState(openOnInit);

    useImperativeHandle(ref, () => ({
      close() {
        setOpen(false);
      },
      open() {
        setOpen(true);
      },
    }));

    useEffect(() => {
      if (open !== undefined && onToggle) {
        onToggle(open);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    /**
     * Handles only esc key
     * (intended to be set as listener on document)
     * @param event
     */
    const handleEsc = (event) => {
      if (event && event.key === 'Escape') {
        setOpen(false);
      }
    };

    /**
     * Sets close events for closing dropdowns when clicking outside component
     * @param
     */
    const onDocumentClick = (event: MouseEvent): void => {
      const toggle = closeMenuOnClick ? toggleRef : wrapToggleRef;
      if (
        event.button !== 2 &&
        toggle.current &&
        !toggle.current.contains(event.target as HTMLElement)
      ) {
        setOpen(false);
      }
    };

    useEffect(() => {
      const doc = window.document;
      doc.addEventListener('click', onDocumentClick);
      doc.addEventListener('keyup', handleEsc);

      return () => {
        doc.removeEventListener('click', onDocumentClick);
        doc.removeEventListener('keyup', handleEsc);
      };
    });

    /**
     * Clicking the toggle portion.
     */
    const onToggleClick = () => {
      if (!disabled) {
        setOpen(!open);
      }
    };

    const withSpacing = () => {
      if (error) {
        return { marginTop: '20px' };
      }

      return {};
    };

    return (
      <div
        className={classnames('dropdown', className)}
        data-testid="dropdown"
        ref={wrapToggleRef}
      >
        <div
          data-testid="dropdown-toggle"
          className="toggle"
          ref={toggleRef}
          onMouseDown={onToggleClick}
          onKeyUp={handleEsc}
          role="listbox"
          tabIndex={0}
          aria-expanded={open}
          aria-controls="dropdown-menu"
        >
          {toggle({ expanded: open })}
        </div>
        <div
          data-testid="dropdown-menu"
          id="dropdown-menu"
          className={classnames('menu', {
            fullWidth: isFullWidth,
            isMenuRightAligned,
            isMenuCentered,
            isOpened: open,
            hidden: isPortalMenu,
          })}
          style={{
            ...getTypeaheadStyle(wrapToggleRef.current),
            ...(maxMenuHeight && { maxHeight: maxMenuHeight }),
            ...withSpacing(),
            ...(menuWidth && { width: menuWidth }),
          }}
        >
          {menu({ show: open })}
        </div>
      </div>
    );
  }
);

Dropdown.displayName = 'Dropdown';
